"use client";

import { useState, useTransition } from "react";

interface Props {
  siteId: string;
  intentOptions: string[];
  defaultIntent?: string;
}

interface Attribution {
  utm: Record<string, string>;
  referrer: string | null;
}

function readAttribution(): Attribution {
  if (typeof window === "undefined") {
    return { utm: {}, referrer: null };
  }
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
    const v = params.get(k);
    if (v) {
      utm[k] = v;
    }
  }
  return { utm, referrer: document.referrer || null };
}

export function LeadFormInteractive({ siteId, intentOptions, defaultIntent }: Props) {
  const [status, setStatus] = useState<"idle" | "ok" | "error">("idle");
  const [pending, startTransition] = useTransition();
  // Read once at mount and freeze — typing should not re-read attribution.
  const [attribution] = useState<Attribution>(() => readAttribution());

  if (status === "ok") {
    return (
      <div className="mx-auto max-w-[760px] rounded-(--radius-sm) border border-(--color-border) bg-(--color-surface-2) px-6 py-4 text-center text-sm">
        Thanks — we&apos;ll text you back shortly.
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        startTransition(async () => {
          try {
            const res = await fetch("/api/leads", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                siteId,
                fullName: String(fd.get("name") ?? ""),
                phone: String(fd.get("phone") ?? ""),
                intent: String(fd.get("intent") ?? ""),
                source: attribution,
                honeypot: String(fd.get("hp_email") ?? ""),
              }),
            });
            if (res.ok) {
              setStatus("ok");
            } else {
              setStatus("error");
            }
          } catch {
            setStatus("error");
          }
        });
      }}
      className="mx-auto grid max-w-[760px] grid-cols-1 gap-2.5 md:grid-cols-[1fr_1fr_1fr_auto]"
    >
      <input type="hidden" name="siteId" value={siteId} />
      <input
        type="text"
        name="hp_email"
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
        aria-hidden="true"
      />
      <input
        type="text"
        name="name"
        placeholder="Your name"
        required
        className="rounded-(--radius-sm) border border-(--color-border-strong) bg-(--color-background) px-3 py-2.5 text-[13.5px]"
      />
      <input
        type="tel"
        name="phone"
        placeholder="Phone number"
        required
        className="rounded-(--radius-sm) border border-(--color-border-strong) bg-(--color-background) px-3 py-2.5 text-[13.5px]"
      />
      <select
        name="intent"
        defaultValue={defaultIntent ?? intentOptions[0]}
        className="rounded-(--radius-sm) border border-(--color-border-strong) bg-(--color-background) px-3 py-2.5 text-[13.5px]"
      >
        {intentOptions.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      <button
        type="submit"
        disabled={pending}
        className="rounded-(--radius-sm) bg-(--color-brand-accent) px-5 py-2.5 text-sm font-medium text-(--color-on-accent) disabled:opacity-50"
      >
        {pending ? "Sending..." : "Send →"}
      </button>
      {status === "error" ? (
        <p className="col-span-full text-xs text-red-500">
          Something went wrong. Please call us directly.
        </p>
      ) : null}
    </form>
  );
}
